import React, { useState } from "react";
import { useNavigate } from "react-router-dom"; 
import { createUserWithEmailAndPassword, updateProfile } from "firebase/auth";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { auth, db } from "../firebase"; // Import from your config
import { 
  Container, Paper, Typography, TextField, Button, Box, Alert 
} from "@mui/material";

function Register() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
    first_name: "", 
    last_name: ""   
  });

  const [message, setMessage] = useState("");
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");
    setError(false);
    setLoading(true);

    try {
      // 1. Create the Authentication User
      const userCredential = await createUserWithEmailAndPassword(
        auth, 
        formData.email, 
        formData.password
      );
      const user = userCredential.user;

      // 2. Set the "Display Name" on the Auth object (useful for simple headers)
      await updateProfile(user, { displayName: formData.username });

      // 3. Create the User Document in Firestore
      // We map "first_name" -> "firstName" to keep the database standard camelCase
      await setDoc(doc(db, "users", user.uid), {
        username: formData.username,
        email: formData.email,
        firstName: formData.first_name, 
        lastName: formData.last_name,
        role: "user", // Default role
        createdAt: serverTimestamp(),
        recipeCount: 0,
        photoUrl: ""
      });
      
      setMessage("User registered successfully! Redirecting...");
      
      // Short delay to show success message before redirect
      setTimeout(() => navigate("/recipes"), 1500);

    } catch (err) {
      console.error("Registration Error:", err);
      setError(true);
      
      // Handle standard Firebase errors
      if (err.code === "auth/email-already-in-use") {
        setMessage("This email is already registered.");
      } else if (err.code === "auth/weak-password") {
        setMessage("Password must be at least 6 characters.");
      } else {
        setMessage("Registration failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Paper elevation={3} sx={{ p: 4, mt: 8 }}>
        <Typography variant="h4" component="h1" gutterBottom align="center">
          Create Account
        </Typography>
        
        <form onSubmit={handleSubmit}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            
            {/* First & Last Name */}
            <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                    label="First Name"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleChange}
                    fullWidth
                />
                <TextField
                    label="Last Name"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleChange}
                    fullWidth
                />
            </Box>

            <TextField
              label="Username"
              name="username"
              value={formData.username}
              onChange={handleChange}
              required
              fullWidth
            />
            
            <TextField
              label="Email"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              fullWidth
            />

            <TextField
              label="Password"
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              fullWidth
              helperText="Must be at least 6 characters"
            />

            <Button 
              type="submit" 
              variant="contained" 
              size="large" 
              fullWidth 
              sx={{ mt: 2 }}
              disabled={loading}
            >
              {loading ? "Creating Account..." : "Register"}
            </Button>
            
            <Button 
                onClick={() => navigate("/login")}
                sx={{ textTransform: 'none' }}
            >
                Already have an account? Log In
            </Button>
          </Box>
        </form>

        {message && (
          <Alert severity={error ? "error" : "success"} sx={{ mt: 2 }}>
            {message}
          </Alert>
        )}
      </Paper>
    </Container>
  );
}

export default Register;